import { CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

const OPTIONS_REQUIRED_MESSAGE = 'Please select some product options before adding this product to your cart.';

class ProductAddOns extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    queueMicrotask(() => this.moveUnderProductBuyButtons());

    this.track = this.querySelector('[data-add-ons-track]');
    this.prevButton = this.querySelector('[data-add-ons-prev]');
    this.nextButton = this.querySelector('[data-add-ons-next]');

    this.prevButton?.addEventListener('click', () => this.scrollCards(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.scrollCards(1), { signal });
    this.addEventListener('submit', this.handleSubmit, { signal });
    this.addEventListener('change', this.handleVariantChange, { signal });

    this.updateArrows();
    this.track?.addEventListener('scroll', () => this.updateArrows(), { passive: true, signal });
    window.addEventListener('resize', this.updateArrows, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  moveUnderProductBuyButtons() {
    const sectionWrapper = this.closest('.shopify-section');
    const section = this.closest('.product-add-ons');
    const buyButtons = document.querySelector('.product-details .buy-buttons-block');

    if (section) this.removeOtherAddOns(section);

    if (!section || !buyButtons || buyButtons.nextElementSibling === section) return;

    sectionWrapper?.classList.add('product-add-ons-wrapper--in-product-details');
    section.classList.add('product-add-ons--in-product-details');
    buyButtons.insertAdjacentElement('afterend', section);
  }

  removeOtherAddOns(currentSection) {
    const addOnSections = document.querySelectorAll('.product-add-ons');

    addOnSections.forEach((section) => {
      if (section !== currentSection) section.remove();
    });
  }

  scrollCards(direction) {
    const card = this.track?.querySelector('.product-add-ons__card');
    if (!this.track || !card) return;

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({
      left: direction * (card.getBoundingClientRect().width + gap),
      behavior: 'smooth',
    });
  }

  updateArrows = () => {
    if (!this.track || !this.prevButton || !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const hasOverflow = maxScroll > 1;
    this.prevButton.hidden = !hasOverflow;
    this.nextButton.hidden = !hasOverflow;
    this.prevButton.disabled = this.track.scrollLeft <= 1;
    this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 1;
  };

  handleVariantChange = (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches('[data-add-ons-variant-select]')) return;

    const form = select.closest('form');
    const button = form?.querySelector('[data-add-ons-submit]');
    const price = form?.closest('.product-add-ons__card')?.querySelector('[data-add-ons-price]');
    const option = select.selectedOptions[0];
    const available = option?.dataset.available === 'true';

    if (button instanceof HTMLButtonElement) {
      button.disabled = !available;
      button.textContent = available ? button.dataset.defaultText || 'Add to cart' : 'Sold out';
    }

    if (price && option?.dataset.price) {
      price.textContent = option.dataset.price;
    }
  };

  handleSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-add-ons-form]')) return;
    event.preventDefault();

    const form = event.target;
    const button = form.querySelector('[data-add-ons-submit]');
    const status = form.querySelector('[data-add-ons-status]');
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;

    const variantSelect = form.querySelector('[data-add-ons-variant-select]');
    if (variantSelect instanceof HTMLSelectElement && !variantSelect.value) {
      this.showOptionsRequiredDialog();
      return;
    }

    const originalText = button.dataset.defaultText || button.textContent || 'Add to cart';
    button.disabled = true;
    button.textContent = 'Adding...';
    if (status) status.textContent = '';

    try {
      const formData = new FormData(form);
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIds = [];

      cartItemsComponents.forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
      });

      if (sectionIds.length) {
        formData.append('sections', sectionIds.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
        },
      });
      const addedItem = await response.json();

      if (!response.ok || addedItem.status) {
        throw new Error(addedItem.description || addedItem.message || 'Add failed');
      }

      const cartResponse = await fetch(Theme.routes.cart_url + '.js', { credentials: 'same-origin' });
      const cart = await cartResponse.json();

      document.dispatchEvent(
        new CartUpdateEvent(cart, `product-add-ons-${addedItem.id}`, {
          itemCount: cart.item_count,
          productId: form.dataset.productId,
          variantId: String(formData.get('id')),
          sections: addedItem.sections || {},
          source: 'product-add-ons',
          suppressCartDrawerOpen: true,
        })
      );

      button.textContent = 'Added';
      if (status) status.textContent = 'Added to cart';

      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
        if (status) status.textContent = '';
      }, 1600);
    } catch (error) {
      button.textContent = 'Failed';
      if (status) status.textContent = error instanceof Error ? error.message : 'Unable to add item';

      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    }
  };

  showOptionsRequiredDialog() {
    let dialog = document.querySelector('dialog[data-options-required-dialog]');
    if (!(dialog instanceof HTMLDialogElement)) {
      dialog = document.createElement('dialog');
      dialog.className = 'product-options-required-dialog';
      dialog.dataset.optionsRequiredDialog = 'true';
      dialog.setAttribute('aria-label', 'Product options required');

      const message = document.createElement('p');
      message.className = 'product-options-required-dialog__message';
      message.textContent = OPTIONS_REQUIRED_MESSAGE;

      const okButton = document.createElement('button');
      okButton.type = 'button';
      okButton.className = 'button product-options-required-dialog__button';
      okButton.textContent = 'OK';
      okButton.addEventListener('click', () => dialog.close());

      dialog.append(message, okButton);
      document.body.appendChild(dialog);
    }

    if (!dialog.open) dialog.showModal();
    dialog.querySelector('button')?.focus();
  }
}

if (!customElements.get('product-add-ons')) {
  customElements.define('product-add-ons', ProductAddOns);
}
